import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { RefreshCw, CheckCircle, XCircle, Clock, Loader2, Calendar as CalendarIcon, User, Users, Plus, Eye, ArrowLeft } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

interface ShiftSwap {
  id: string;
  requester_id: string;
  requester_assignment_id: string;
  target_id: string;
  target_assignment_id: string;
  reason?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  reviewed_by?: string;
  review_notes?: string;
  reviewed_at?: string;
  created_at: string;
  updated_at: string;
}

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
}

interface ShiftAssignment {
  id: string;
  employee_id: string;
  shift_id: string;
  schedule_id: string;
  assignment_date: string;
  status: string;
}

interface Shift {
  id: string;
  name: string;
  start_time: string;
  end_time: string;
}

const ShiftSwaps: React.FC = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, canApproveShiftSwaps } = useAuth();
  const [employees, setEmployees] = useState<Record<string, Employee>>({});
  // Default to 'user' view for non-managers, 'manager' for managers
  const [viewMode, setViewMode] = useState<'user' | 'manager'>('user');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedSwapRequest, setSelectedSwapRequest] = useState<ShiftSwap | null>(null);
  
  // Set initial view mode based on user's role
  useEffect(() => {
    if (canApproveShiftSwaps) {
      setViewMode('manager');
    } else {
      setViewMode('user');
    }
  }, [canApproveShiftSwaps]);
  
  // Form state for creating swap request
  const [selectedRequester, setSelectedRequester] = useState<string>('');
  const [selectedRequesterAssignment, setSelectedRequesterAssignment] = useState<string>('');
  const [selectedTarget, setSelectedTarget] = useState<string>('');
  const [selectedTargetAssignment, setSelectedTargetAssignment] = useState<string>('');
  const [swapReason, setSwapReason] = useState<string>('');

  // Auto-set the requester to current user when component mounts or user changes
  useEffect(() => {
    if (user?.id) {
      setSelectedRequester(user.id);
    }
  }, [user]);

  // Handle refresh with toast notification
  const handleRefresh = async () => {
    toast({ title: 'Refreshing...', description: 'Fetching latest swap requests' });
    const result = await refetch();
    if (result.isSuccess) {
      toast({ title: 'Refreshed successfully', description: 'Swap requests are up to date' });
    }
  };

  // Fetch shift swaps
  const { data: swapRequests = [], isLoading, refetch, isFetching } = useQuery<ShiftSwap[]>({
    queryKey: ['shiftSwaps'],
    queryFn: async () => (await api.shift.swaps.getAll()) as ShiftSwap[],
  });

  // Fetch assignments to get dates
  const { data: assignments = [] } = useQuery<any[]>({
    queryKey: ['allAssignments'],
    queryFn: async () => (await api.shift.assignments.getAll({})) as any[],
  });

  // Fetch shifts to display shift names
  const { data: shifts = [] } = useQuery<Shift[]>({
    queryKey: ['allShifts'],
    queryFn: async () => (await api.shift.shifts.getAll()) as Shift[],
  });

  // Fetch employees to display names
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const employeeData = (await api.employee.getAll()) as Employee[];
        const employeeMap: Record<string, Employee> = {};
        employeeData.forEach((emp: Employee) => {
          employeeMap[emp.id] = emp;
        });
        setEmployees(employeeMap);
      } catch (error) {
        console.error('Failed to fetch employees:', error);
      }
    };
    fetchEmployees();
  }, []);

  // Approve mutation
  const approveMutation = useMutation({
    mutationFn: (id: string) =>
      api.shift.swaps.approve(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shiftSwaps'] });
      toast({ title: 'Shift swap approved successfully' });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to approve shift swap',
        description: error.message || 'An error occurred',
        variant: 'destructive'
      });
    },
  });

  // Reject mutation
  const rejectMutation = useMutation({
    mutationFn: (id: string) =>
      api.shift.swaps.reject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shiftSwaps'] });
      toast({ title: 'Shift swap rejected successfully' });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to reject shift swap',
        description: error.message || 'An error occurred',
        variant: 'destructive'
      });
    },
  });

  // Create swap mutation
  const createSwapMutation = useMutation({
    mutationFn: (data: {
      requester_id: string;
      requester_assignment_id: string;
      target_id: string;
      target_assignment_id: string;
      reason?: string;
    }) => api.shift.swaps.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shiftSwaps'] });
      toast({ title: 'Shift swap request created successfully' });
      resetForm();
      setIsCreateDialogOpen(false);
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to create shift swap request',
        description: error.message || 'An error occurred',
        variant: 'destructive'
      });
    },
  });

  const resetForm = () => {
    // Don't reset selectedRequester as it should always be the current user
    setSelectedRequesterAssignment('');
    setSelectedTarget('');
    setSelectedTargetAssignment('');
    setSwapReason('');
  };

  const handleApprove = (id: string) => {
    approveMutation.mutate(id);
  };

  const handleReject = (id: string) => {
    rejectMutation.mutate(id);
  };

  const handleViewSwap = (request: ShiftSwap) => {
    setSelectedSwapRequest(request);
    setIsViewDialogOpen(true);
  };

  const handleCreateSwap = () => {
    if (!selectedRequesterAssignment || !selectedTarget || !selectedTargetAssignment) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      });
      return;
    }

    if (!user?.id) {
      toast({
        title: 'Authentication Error',
        description: 'User not authenticated',
        variant: 'destructive'
      });
      return;
    }

    createSwapMutation.mutate({
      requester_id: user.id,
      requester_assignment_id: selectedRequesterAssignment,
      target_id: selectedTarget,
      target_assignment_id: selectedTargetAssignment,
      reason: swapReason || undefined,
    });
  };

  const getEmployeeName = (employeeId: string): string => {
    const employee = employees[employeeId];
    return employee ? `${employee.first_name} ${employee.last_name}` : 'Unknown Employee';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED': return 'default';
      case 'REJECTED': return 'destructive';
      case 'PENDING': return 'secondary';
      default: return 'outline';
    }
  };

  const getAssignmentDate = (assignmentId: string) => {
    const assignment = assignments.find((a: any) => a.id === assignmentId);
    return assignment ? assignment.work_date : null;
  };

  const getShiftName = (shiftId: string): string => {
    const shift = shifts.find((s: Shift) => s.id === shiftId);
    return shift ? shift.name : 'Unknown Shift';
  };

  const getAssignmentsByEmployee = (employeeId: string): any[] => {
    return assignments.filter((a: any) => a.employee_id === employeeId && a.status !== 'CANCELLED');
  };

  const formatAssignmentOption = (assignment: any): string => {
    const date = assignment.assignment_date || assignment.work_date;
    const shiftName = getShiftName(assignment.shift_id);
    return `${new Date(date).toLocaleDateString()} - ${shiftName}`;
  };

  const getAssignmentDetails = (assignmentId: string) => {
    const assignment = assignments.find((a: any) => a.id === assignmentId);
    if (!assignment) return null;
    
    const date = assignment.assignment_date || assignment.work_date;
    const shift = shifts.find((s: Shift) => s.id === assignment.shift_id);
    
    return {
      date: new Date(date).toLocaleDateString(),
      shiftName: shift?.name || 'Unknown Shift',
      startTime: shift?.start_time || '',
      endTime: shift?.end_time || '',
      assignment
    };
  };

  const employeeList = Object.values(employees);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto space-y-8 p-6 custom-scrollbar animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/shift-planning')}
              className="h-8 w-8 p-0 text-slate-500 hover:text-[#143A62] hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-3xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Shift Swap Requests</h1>
          </div>
          <p className="text-sm font-medium text-slate-500 ml-10">Manage employee shift exchange requests</p>
        </div>
        <div className="flex items-center space-x-3">
          {/* Only show view mode toggle if user can approve shift swaps */}
          {canApproveShiftSwaps && (
            <div className="flex items-center p-1 border rounded-xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-sm">
              <Button
                variant={viewMode === 'user' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('user')}
                className={`rounded-lg px-4 font-bold text-xs ${viewMode === 'user' ? 'bg-[#143A62] text-white hover:bg-[#143A62]/90' : 'text-slate-500 hover:text-[#143A62]'}`}
              >
                <User className="h-4 w-4 mr-1.5" />
                My Swaps
              </Button>
              <Button
                variant={viewMode === 'manager' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('manager')}
                className={`rounded-lg px-4 font-bold text-xs ${viewMode === 'manager' ? 'bg-[#143A62] text-white hover:bg-[#143A62]/90' : 'text-slate-500 hover:text-[#143A62]'}`}
              >
                <Users className="h-4 w-4 mr-1.5" />
                Manager View
              </Button>
            </div>
          )}
          <Button onClick={handleRefresh} variant="outline" className="border-slate-200 text-slate-700 hover:bg-slate-50" disabled={isFetching}>
            <RefreshCw className={`h-4 w-4 mr-1.5 ${isFetching ? 'animate-spin' : ''}`} />
            {isFetching ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="">
                <Plus className="h-4 w-4 mr-1.5 shrink-0" />
                New Swap Request
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] border-slate-100 dark:border-slate-800 shadow-xl rounded-2xl p-0 overflow-hidden flex flex-col max-h-[90vh]">
              <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-50 dark:border-slate-800/50 bg-slate-50/50 dark:bg-slate-900/50 shrink-0">
                <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Create Shift Swap Request</DialogTitle>
                <DialogDescription className="text-slate-500 font-medium mt-1.5">
                  Request to swap your shift with another employee. Select both shifts to swap.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-5 py-4 px-6 overflow-y-auto custom-scrollbar">
                {/* Requester Display - Show current user's name */}
                <div className="grid gap-2">
                  <Label htmlFor="requester" className="text-[13px] font-bold text-[#143A62] dark:text-slate-300">Your Name (Requester)</Label>
                  <div className="flex items-center h-11 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 px-3 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
                    <User className="h-4 w-4 mr-2 text-slate-400" />
                    {user ? `${user.first_name} ${user.last_name}` : 'Loading...'}
                  </div>
                </div>

                {/* Requester's Shift Assignment */}
                <div className="grid gap-2">
                  <Label htmlFor="requesterAssignment" className="text-[13px] font-bold text-[#143A62] dark:text-slate-300">Your Shift to Swap</Label>
                  <Select 
                    value={selectedRequesterAssignment} 
                    onValueChange={setSelectedRequesterAssignment}
                  >
                    <SelectTrigger className="h-11 rounded-xl border-slate-200 dark:border-slate-700">
                      <SelectValue placeholder={user?.id ? "Select your shift" : "Loading user data..."} />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl border-slate-100 shadow-lg">
                      {user?.id && getAssignmentsByEmployee(user.id).map((assignment) => (
                        <SelectItem key={assignment.id} value={assignment.id} className="cursor-pointer font-medium">
                          {formatAssignmentOption(assignment)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Target Employee Selection */}
                <div className="grid gap-2">
                  <Label htmlFor="target" className="text-[13px] font-bold text-[#143A62] dark:text-slate-300">Swap With (Target Employee)</Label>
                  <Select value={selectedTarget} onValueChange={(value) => {
                    setSelectedTarget(value);
                    setSelectedTargetAssignment('');
                  }}>
                    <SelectTrigger className="h-11 rounded-xl border-slate-200 dark:border-slate-700">
                      <SelectValue placeholder="Select target employee" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl border-slate-100 shadow-lg max-h-60">
                      {employeeList
                        .filter((emp) => emp.id !== user?.id)
                        .map((emp) => (
                          <SelectItem key={emp.id} value={emp.id} className="cursor-pointer font-medium">
                            {emp.first_name} {emp.last_name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Target's Shift Assignment */}
                <div className="grid gap-2">
                  <Label htmlFor="targetAssignment" className="text-[13px] font-bold text-[#143A62] dark:text-slate-300">Their Shift (to take)</Label>
                  <Select 
                    value={selectedTargetAssignment} 
                    onValueChange={setSelectedTargetAssignment}
                  >
                    <SelectTrigger className="h-11 rounded-xl border-slate-200 dark:border-slate-700">
                      <SelectValue placeholder={selectedTarget ? "Select their shift" : "Select target employee first"} />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl border-slate-100 shadow-lg max-h-60">
                      {selectedTarget && getAssignmentsByEmployee(selectedTarget).map((assignment) => (
                        <SelectItem key={assignment.id} value={assignment.id} className="cursor-pointer font-medium">
                          {formatAssignmentOption(assignment)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Reason */}
                <div className="grid gap-2">
                  <Label htmlFor="reason" className="text-[13px] font-bold text-[#143A62] dark:text-slate-300">Reason (Optional)</Label>
                  <Textarea
                    id="reason"
                    placeholder="Why do you need to swap this shift?"
                    value={swapReason}
                    onChange={(e) => setSwapReason(e.target.value)}
                    rows={3}
                    className="rounded-xl border-slate-200 dark:border-slate-700 resize-none"
                  />
                </div>
              </div>
              <DialogFooter className="px-6 py-4 border-t border-slate-50 dark:border-slate-800/50 bg-slate-50/50 dark:bg-slate-900/50 sm:justify-end shrink-0">
                <Button variant="outline" className="rounded-xl border-slate-200" onClick={() => {
                  resetForm();
                  setIsCreateDialogOpen(false);
                }}>
                  Cancel
                </Button>
                <Button 
                  className="rounded-xl bg-[#143A62] text-white hover:bg-[#143A62]/90 shadow-sm"
                  onClick={handleCreateSwap} 
                  disabled={createSwapMutation.isPending || !selectedRequesterAssignment || !selectedTarget || !selectedTargetAssignment}
                >
                  {createSwapMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Submit Request'
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* View Swap Details Dialog */}
          <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
            <DialogContent className="sm:max-w-[600px] border-slate-100 dark:border-slate-800 shadow-xl rounded-2xl p-0 overflow-hidden flex flex-col max-h-[90vh]">
              <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-50 dark:border-slate-800/50 bg-slate-50/50 dark:bg-slate-900/50 shrink-0">
                <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Shift Swap Request Details</DialogTitle>
                <DialogDescription className="text-slate-500 font-medium mt-1.5">
                  Complete information about this shift swap request
                </DialogDescription>
              </DialogHeader>
              {selectedSwapRequest && (
                <div className="grid gap-8 py-6 px-6 overflow-y-auto custom-scrollbar">
                  {/* Request Information */}
                  <div className="grid gap-3">
                    <h3 className="text-[11px] font-bold text-slate-400 tracking-wider uppercase">Request Information</h3>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-5 bg-slate-50 dark:bg-slate-800/30 p-4 rounded-xl border border-slate-100 dark:border-slate-800/50">
                      <div>
                        <Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Request ID</Label>
                        <p className="text-[13px] font-bold text-[#143A62] dark:text-slate-300">{selectedSwapRequest.id.substring(0, 8)}...</p>
                      </div>
                      <div>
                        <Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Status</Label>
                        <Badge variant={getStatusColor(selectedSwapRequest.status)} className="px-2 py-0.5 rounded-md font-bold text-[10px]">
                          {selectedSwapRequest.status}
                        </Badge>
                      </div>
                      <div>
                        <Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Created Date</Label>
                        <p className="text-[13px] font-semibold text-slate-700 dark:text-slate-300">{new Date(selectedSwapRequest.created_at).toLocaleDateString()}</p>
                      </div>
                      <div>
                        <Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Last Updated</Label>
                        <p className="text-[13px] font-semibold text-slate-700 dark:text-slate-300">{new Date(selectedSwapRequest.updated_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Requester Information */}
                    <div className="grid gap-3">
                      <h3 className="text-[11px] font-bold text-slate-400 tracking-wider uppercase flex items-center"><ArrowLeft className="h-3 w-3 mr-1 text-blue-500" /> Requester</h3>
                      <div className="bg-blue-50/50 dark:bg-slate-800/30 p-4 rounded-xl border border-blue-100 dark:border-slate-800/50 space-y-4">
                        <div>
                          <Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Employee</Label>
                          <p className="text-sm font-bold text-[#143A62] dark:text-blue-400">{getEmployeeName(selectedSwapRequest.requester_id)}</p>
                        </div>
                        <div>
                          <Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Shift to Give Up</Label>
                          {(() => {
                            const details = getAssignmentDetails(selectedSwapRequest.requester_assignment_id);
                            return details ? (
                              <div className="text-sm">
                                <p className="font-bold text-slate-700 dark:text-slate-300">{details.date}</p>
                                <p className="font-semibold text-blue-600 dark:text-blue-400 text-xs mt-0.5">{details.shiftName}</p>
                                <p className="text-xs font-medium text-slate-500 mt-0.5">{details.startTime} - {details.endTime}</p>
                              </div>
                            ) : (
                              <p className="text-sm text-slate-400 italic">Details not available</p>
                            );
                          })()}
                        </div>
                      </div>
                    </div>

                    {/* Target Employee Information */}
                    <div className="grid gap-3">
                      <h3 className="text-[11px] font-bold text-slate-400 tracking-wider uppercase flex items-center">Target <ArrowLeft className="h-3 w-3 ml-1 text-indigo-500 transform rotate-180" /></h3>
                      <div className="bg-indigo-50/50 dark:bg-slate-800/30 p-4 rounded-xl border border-indigo-100 dark:border-slate-800/50 space-y-4">
                        <div>
                          <Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Employee</Label>
                          <p className="text-sm font-bold text-indigo-700 dark:text-indigo-400">{getEmployeeName(selectedSwapRequest.target_id)}</p>
                        </div>
                        <div>
                          <Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Shift to Take</Label>
                          {(() => {
                            const details = getAssignmentDetails(selectedSwapRequest.target_assignment_id);
                            return details ? (
                              <div className="text-sm">
                                <p className="font-bold text-slate-700 dark:text-slate-300">{details.date}</p>
                                <p className="font-semibold text-indigo-600 dark:text-indigo-400 text-xs mt-0.5">{details.shiftName}</p>
                                <p className="text-xs font-medium text-slate-500 mt-0.5">{details.startTime} - {details.endTime}</p>
                              </div>
                            ) : (
                              <p className="text-sm text-slate-400 italic">Details not available</p>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Reason */}
                  {selectedSwapRequest.reason && (
                    <div className="grid gap-2">
                      <Label className="text-[11px] font-bold text-slate-400 tracking-wider uppercase">Reason for Swap</Label>
                      <div className="p-4 bg-slate-50 dark:bg-slate-800/30 rounded-xl border border-slate-100 dark:border-slate-800/50">
                        <p className="text-[13px] font-medium text-slate-700 dark:text-slate-300 italic">"{selectedSwapRequest.reason}"</p>
                      </div>
                    </div>
                  )}

                  {/* Review Information */}
                  {(selectedSwapRequest.reviewed_by || selectedSwapRequest.review_notes) && (
                    <div className="grid gap-3 pt-4 border-t border-slate-100 dark:border-slate-800/50">
                      <h3 className="text-[11px] font-bold text-slate-400 tracking-wider uppercase">Review Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {selectedSwapRequest.reviewed_by && (
                          <div>
                            <Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Reviewed By</Label>
                            <p className="text-[13px] font-bold text-[#143A62] dark:text-slate-300">{getEmployeeName(selectedSwapRequest.reviewed_by)}</p>
                          </div>
                        )}
                        {selectedSwapRequest.reviewed_at && (
                          <div>
                            <Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Review Date</Label>
                            <p className="text-[13px] font-semibold text-slate-700 dark:text-slate-300">{new Date(selectedSwapRequest.reviewed_at).toLocaleDateString()}</p>
                          </div>
                        )}
                        {selectedSwapRequest.review_notes && (
                          <div className="col-span-full">
                            <Label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-1">Review Notes</Label>
                            <div className="p-3 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                              <p className="text-[13px] text-slate-600 dark:text-slate-300">{selectedSwapRequest.review_notes}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <DialogFooter className="px-6 py-4 border-t border-slate-50 dark:border-slate-800/50 bg-slate-50/50 dark:bg-slate-900/50 sm:justify-end shrink-0">
                {selectedSwapRequest && viewMode === 'manager' && selectedSwapRequest.status === 'PENDING' && canApproveShiftSwaps && (
                  <div className="flex space-x-2 w-full sm:w-auto mb-2 sm:mb-0 sm:mr-auto">
                    <Button
                      variant="outline"
                      onClick={() => {
                        handleReject(selectedSwapRequest.id);
                        setIsViewDialogOpen(false);
                      }}
                      className="text-rose-600 hover:text-rose-700 border-rose-200 hover:bg-rose-50"
                      disabled={rejectMutation.isPending}
                    >
                      <XCircle className="h-4 w-4 mr-1.5" />
                      Reject
                    </Button>
                    <Button
                      onClick={() => {
                        handleApprove(selectedSwapRequest.id);
                        setIsViewDialogOpen(false);
                      }}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      disabled={approveMutation.isPending}
                    >
                      <CheckCircle className="h-4 w-4 mr-1.5" />
                      Approve
                    </Button>
                  </div>
                )}
                <Button variant="outline" className="border-slate-200" onClick={() => setIsViewDialogOpen(false)}>
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {viewMode === 'manager' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 px-6 pt-5">
              <CardTitle className="text-xs font-bold text-slate-400 tracking-wider uppercase">Total Requests</CardTitle>
              <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-blue-500 shrink-0">
                <RefreshCw className="h-4 w-4" />
              </div>
            </CardHeader>
            <CardContent className="px-6 pb-5">
              <div className="text-3xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">{swapRequests.length}</div>
            </CardContent>
          </Card>

          <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 px-6 pt-5">
              <CardTitle className="text-xs font-bold text-slate-400 tracking-wider uppercase">Pending</CardTitle>
              <div className="w-8 h-8 rounded-lg bg-orange-50 dark:bg-orange-900/30 flex items-center justify-center text-orange-500 shrink-0">
                <Clock className="h-4 w-4" />
              </div>
            </CardHeader>
            <CardContent className="px-6 pb-5">
              <div className="text-3xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">
                {swapRequests.filter(r => r.status === 'PENDING').length}
              </div>
            </CardContent>
          </Card>

          <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 rounded-2xl overflow-hidden hover:shadow-lg transition-all duration-300">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 px-6 pt-5">
              <CardTitle className="text-xs font-bold text-slate-400 tracking-wider uppercase">Approved</CardTitle>
              <div className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-500 shrink-0">
                <CheckCircle className="h-4 w-4" />
              </div>
            </CardHeader>
            <CardContent className="px-6 pb-5">
              <div className="text-3xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">
                {swapRequests.filter(r => r.status === 'APPROVED').length}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden rounded-2xl">
        <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50">
          <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">
            {viewMode === 'manager' ? 'All Swap Requests' : 'My Swap Requests'}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {swapRequests.length === 0 ? (
            <div className="text-center py-20 px-8 text-slate-400 font-semibold text-xs">
              No shift swap requests found.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-b-2 border-slate-55 dark:border-slate-800">
                  <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 px-8 w-[120px]">Date</TableHead>
                  <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Requester</TableHead>
                  <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Target Employee</TableHead>
                  <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Reason</TableHead>
                  <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Status</TableHead>
                  <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 px-8">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {swapRequests.map((request) => {
                  const date = getAssignmentDate(request.requester_assignment_id);
                  return (
                    <TableRow key={request.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors border-b border-slate-50 dark:border-slate-800/50">
                      <TableCell className="px-8 py-4">
                        {date ? (
                          <div className="flex flex-col items-center justify-center bg-blue-50/50 dark:bg-slate-800/50 border border-blue-100 dark:border-slate-700 rounded-lg p-2 w-16">
                            <span className="text-[10px] uppercase text-blue-600 dark:text-blue-400 font-bold tracking-wider">{new Date(date).toLocaleDateString('en-US', { month: 'short' })}</span>
                            <span className="font-bold text-lg text-[#143A62] dark:text-slate-200">{new Date(date).getDate()}</span>
                          </div>
                        ) : (
                          <span className="text-slate-400 font-medium">-</span>
                        )}
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="flex items-center space-x-2.5">
                          <div className="w-7 h-7 rounded-lg bg-[#143A62]/10 dark:bg-blue-650/20 text-[#143A62] dark:text-blue-400 font-bold text-[11px] flex items-center justify-center tracking-wider shrink-0 uppercase">
                            {getEmployeeName(request.requester_id).split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </div>
                          <div>
                            <span className="font-bold text-xs text-[#143A62] dark:text-blue-400">{getEmployeeName(request.requester_id)}</span>
                            <div className="text-[10px] font-semibold text-slate-400 mt-0.5">
                              ID: {request.requester_assignment_id.substring(0, 8)}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="flex items-center space-x-2.5">
                          <div className="w-7 h-7 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 font-bold text-[11px] flex items-center justify-center tracking-wider shrink-0 uppercase">
                            {getEmployeeName(request.target_id).split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </div>
                          <div>
                            <span className="font-bold text-xs text-indigo-700 dark:text-indigo-400">{getEmployeeName(request.target_id)}</span>
                            <div className="text-[10px] font-semibold text-slate-400 mt-0.5">
                              ID: {request.target_assignment_id.substring(0, 8)}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="max-w-[200px] truncate text-xs font-medium text-slate-600 dark:text-slate-300" title={request.reason}>
                          {request.reason || <span className="text-slate-400 italic">No reason provided</span>}
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        <Badge variant={getStatusColor(request.status)} className="px-2.5 py-0.5 rounded-lg font-bold text-[10px] tracking-wide border shadow-none shrink-0">
                          {request.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-8 py-4">
                        {viewMode === 'manager' && request.status === 'PENDING' && canApproveShiftSwaps ? (
                          <div className="flex items-center space-x-1.5">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewSwap(request)}
                              className="text-xs font-semibold text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Eye className="h-3.5 w-3.5 mr-1.5" />
                              View
                            </Button>
                            <div className="h-4 w-px bg-slate-200 dark:bg-slate-700 mx-1"></div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg"
                              onClick={() => handleApprove(request.id)}
                              disabled={approveMutation.isPending}
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50 rounded-lg"
                              onClick={() => handleReject(request.id)}
                              disabled={rejectMutation.isPending}
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewSwap(request)}
                            className="text-xs font-semibold text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Eye className="h-3.5 w-3.5 mr-1.5" />
                            View Details
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ShiftSwaps;